import { combineLatest, concatMap, map } from "rxjs/operators";
import each from "lodash-es/each";
import remove from "lodash-es/remove";
import trim from "lodash-es/trim";
import { ApiService } from "../core/api.service";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { PatientAnswer } from "./patient-answer";
import { SurveyData } from "./survey-data";
import { SurveyQuestion } from "./survey-models";
import { SurveySection } from "./survey-section";

declare const moment;

export class SurveySummary {
  surveyData: SurveyData;
  patientSurveys: any;

  isAlreadyConfirmed(): boolean {
    return (
      !this.surveyData.allowMultiplePerPatient &&
      this.patientSurveys.length > 0 &&
      this.patientSurveys[0].confirmed_at != null
    );
  }
}

export const healthHistoryAdultSurveyId = 1;
export const healthHistoryPediatricSurveyId = 2;

@Injectable()
export class SurveyService {
  private surveySummaryStreams = {};

  constructor(private apiService: ApiService) {}

  getSurveyById(surveyId: number): Observable<SurveySummary> {
    if (this.surveySummaryStreams[surveyId] == null) {
      const surveySummaryStream = this.apiService.get(`/api/v2/surveys/${surveyId}`).pipe(
        combineLatest(this.apiService.get(`/api/v2/patient/patient_surveys?survey_id=${surveyId}`)),
        map(vals => {
          const surveyInfo = new SurveySummary();
          surveyInfo.surveyData = SurveyData.fromApiV2(vals[0]);
          surveyInfo.patientSurveys = vals[1];
          return surveyInfo;
        })
      );
      this.surveySummaryStreams[surveyId] = surveySummaryStream;
    }
    return this.surveySummaryStreams[surveyId];
  }

  submitSurvey(surveyData: SurveyData, taskId: number = undefined) {
    return this.createPatientSurvey(surveyData).pipe(
      concatMap(response => {
        return this.submitSurveyAnswers(response.id, surveyData, taskId);
      })
    );
  }

  private createPatientSurvey(surveyData: SurveyData): Observable<any> {
    return this.apiService.post(`/api/v2/patient/patient_surveys`, { survey_id: surveyData.id });
  }

  private submitSurveyAnswers(patientSurveyId: number, surveyData: SurveyData, taskId: number) {
    const surveyAnswers = this.buildSurveyAnswers(surveyData);

    return this.apiService.patch(`/api/v2/patient/patient_surveys/${patientSurveyId}`, {
      confirmed_at: moment().format(),
      survey_answers_attributes: surveyAnswers.map(answer => answer.toApiV2()),
      task_id: taskId,
    });
  }

  private buildSurveyAnswers(surveyData: SurveyData) {
    let patientAnswers: PatientAnswer[] = [];
    each(surveyData.sections, (section: SurveySection) => {
      each(section.questions, (question: SurveyQuestion) => {
        patientAnswers = patientAnswers.concat(this.extractQuestionAnswers(question));
      });
    });

    remove(patientAnswers, answer => {
      return answer.id == null && trim(answer.answerText).length == 0;
    });

    return patientAnswers;
  }

  private extractQuestionAnswers(question: SurveyQuestion): PatientAnswer[] {
    let patientAnswers: PatientAnswer[] = [];
    patientAnswers = patientAnswers.concat(question.patientAnswers);
    if (question.getDependentQuestion()) {
      patientAnswers = patientAnswers.concat(
        this.extractQuestionAnswers(question.getDependentQuestion())
      );
    }
    return patientAnswers;
  }
}
